import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Filters } from "@portal/app/_common/types/dreamSearch";
import {
  FilterKey,
  FilterTranslatorFunction,
  queryParamKeyToFilterKeyMap,
  shouldSetQueryParam,
  translateFiltersToQueryParams,
} from "./queryParamFiltersForDreamSearch";
import { usePathname } from "next/navigation";
import { DREAM_SEARCH_LOCATION_PROMPT_PARAM } from "@portal/app/_common/lib/dreamSearchRoutes";

export const emptyFilters: Required<Filters> = {
  program_duration_days_lte: 0,
  program_environment: [],
  program_exclude_environment: [],
  program_exclude_requirement: [],
  program_name: "",
  program_payment_options_type_in: [],
  program_tags_in: [],
  program_vocation_in: [],
  program_vocation_slug_in: [],
  program_vocation_industry: [],
  provider_exclude_id_in: [],
  provider_name_search_for: [],
  provider_state_in: [],
  program_level_in: [],
  provider_only_partnered: false,
  program_vocation_subindustries_in: [],
};

// Custom hook to manage state for the filters from DreamSearch.
export const useQueryParamFiltersForDreamSearch = (
  defaultFilters: Filters = {},
  initialUserFilters: Filters = {},
  { shouldPromptForLocation = false }: { shouldPromptForLocation?: boolean } = {}
): [Filters, Dispatch<SetStateAction<Filters>>] => {
  const pathname = usePathname();
  const [filters, setFilters] = useState<Filters>({
    ...emptyFilters,
    ...initialUserFilters,
  });

  const updateUrl = useCallback(() => {
    const currentQueryParams = new URLSearchParams(window.location.search);
    const newQueryParams = new URLSearchParams();

    // Loop through filters and update recognized parameters
    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      const queryKey = Object.entries(queryParamKeyToFilterKeyMap).find(
        ([_queryKey, value]) => value === filterKey
      )?.[0];

      const filterToQueryFunction = translateFiltersToQueryParams[
        filterKey as FilterKey
      ] as FilterTranslatorFunction;

      if (filterToQueryFunction) {
        const queryParamEquivalentOfFilterValue = filterToQueryFunction(filterValue);

        if (
          shouldSetQueryParam(filterKey as FilterKey, filterValue, defaultFilters) &&
          queryParamEquivalentOfFilterValue
        ) {
          if (Array.isArray(queryParamEquivalentOfFilterValue)) {
            queryParamEquivalentOfFilterValue.forEach((value: string) => {
              newQueryParams.append(queryKey as string, value);
            });
          } else {
            newQueryParams.set(queryKey as string, queryParamEquivalentOfFilterValue);
          }
        }
      }
    });

    // Retain every occurrence of unrecognized parameters. Collapsing repeated
    // ZIPs to one value would turn an ambiguous location into a valid override.
    currentQueryParams.forEach((value, key) => {
      if (!Object.keys(queryParamKeyToFilterKeyMap).includes(key)) {
        newQueryParams.append(key, value);
      }
    });

    const hasStalePromptMarker =
      !shouldPromptForLocation && currentQueryParams.has(DREAM_SEARCH_LOCATION_PROMPT_PARAM);
    if (!shouldPromptForLocation) newQueryParams.delete(DREAM_SEARCH_LOCATION_PROMPT_PARAM);

    const params = newQueryParams.toString();
    const newUrl = `${pathname}${params ? `?${params}` : ""}`;

    if (newUrl !== window.location.pathname + window.location.search) {
      // Keep the synthetic landing in one history entry until its ZIP is replaced.
      if (shouldPromptForLocation || hasStalePromptMarker) {
        window.history.replaceState({}, "", newUrl);
      } else {
        window.history.pushState({}, "", newUrl);
      }
    }
  }, [filters, defaultFilters, pathname, shouldPromptForLocation]);

  useEffect(() => {
    updateUrl();
  }, [filters, defaultFilters, updateUrl]);

  return [filters, setFilters];
};
